import {useState} from 'react';
import throttle from 'lodash/throttle';
import DOMUtils from '@spothero/utils/dom';

/**
 * This behavior is seen across the Transient/Monthly/Event Packages UI,
 * on the spot list and spot details containers.
 * There is initially an element containing context or summary information
 * towards the top, followed by a lot of scrollable content
 * (e.g. spot cards or various types of spot details information).
 *
 * Once the user has scrolled far enough that this initial context element
 * has disappeared from view, we display a condensed version that sticks
 * to the top of the container.
 * @param {string} initialContextSelector - selector for an element to track.
 * if multiple matches are found, takes the first one.
 * @returns {Array} containing a boolean (whether to show the condensed
 * version of the element) and a throttled function that tracks the visibility
 * of the initial element relative to the viewport, as the user scrolls.
 */
const useStickyScroll = initialContextSelector => {
    const [
        showStickyCondensedElement,
        setShowStickyCondensedElement,
    ] = useState(false);

    const onScroll = () => {
        // Add defensive check to prevent errors when DOMUtils.el is undefined
        if (!DOMUtils?.el) {
            return;
        }

        const initialContextElement =
            DOMUtils.el(initialContextSelector)?.[0] ||
            DOMUtils.el(initialContextSelector);

        // Early return if element is not found
        if (!initialContextElement) {
            return;
        }

        const {top: initialContextElTop} = DOMUtils.getDocumentOffset(
            initialContextElement
        );
        const initialContextElHeight = DOMUtils.height(initialContextElement);
        const initialContextElBottom =
            initialContextElTop + initialContextElHeight;
        const headerHeight = DOMUtils.height('.Header');

        setShowStickyCondensedElement(initialContextElBottom < headerHeight);
    };

    const throttledOnScroll = throttle(onScroll, 500);

    return [showStickyCondensedElement, throttledOnScroll];
};

export default useStickyScroll;
